'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import AppLogo from '@/components/ui/AppLogo';
import LoginForm from './LoginForm';
import EmployerRegisterForm from './EmployerRegisterForm';
import HomeManagerRegisterForm from './HomeManagerRegisterForm';
import { Users, Home, Star, Shield, TrendingUp } from 'lucide-react';
import { useSiteContent } from '@/hooks/useSiteContent';

type AuthMode = 'login' | 'register-employer' | 'register-hm';

export default function AuthPageClient() {
  const [mode, setMode] = useState<AuthMode>('login');
  const { p, maidBg } = useSiteContent();

  const features = [
    { icon: Shield, key: 'auth.feature1' },
    { icon: Star, key: 'auth.feature2' },
    { icon: TrendingUp, key: 'auth.feature3' },
    { icon: Users, key: 'auth.feature4' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-[42%] flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Maid background image */}
        <div className="absolute inset-0">
          <Image
            src={maidBg}
            alt="Professional home manager"
            fill
            className="object-cover object-center"
            priority
          />
          {/* Dark overlay gradient for readability */}
          <div className="absolute inset-0" style={{background: 'linear-gradient(160deg, rgba(12,30,53,0.88) 0%, rgba(26,10,0,0.82) 50%, rgba(13,13,13,0.90) 100%)'}} />
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{background: 'rgba(232,93,4,0.10)'}} />
          <div className="absolute -bottom-32 -left-16 w-80 h-80 rounded-full" style={{background: 'rgba(26,46,26,0.5)'}} />
          <div className="absolute top-1/2 -right-10 w-48 h-48 rounded-full" style={{background: 'rgba(255,140,0,0.06)'}} />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={44} />
            <span className="font-extrabold text-2xl text-white tracking-tight">{p('auth.brand.name')}</span>
          </div>
          <h1 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight mb-4">
            {p('auth.hero.title.line1')}<br />
            <span style={{color: 'var(--primary)'}}>{p('auth.hero.title.highlight')}</span> {p('auth.hero.title.line2')}
          </h1>
          <p className="text-sm leading-relaxed mb-10" style={{color: 'rgba(245,240,232,0.70)'}}>
            {p('auth.hero.subtitle')}
          </p>
          <div className="space-y-4">
            {features.map((f) => (
              <div key={f.key} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{background: 'rgba(232,93,4,0.18)', border: '1px solid rgba(232,93,4,0.30)'}}>
                  <f.icon size={16} style={{color: 'var(--accent)'}} />
                </div>
                <p className="text-sm leading-relaxed" style={{color: 'rgba(245,240,232,0.80)'}}>{p(f.key)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-4 p-4 rounded-xl backdrop-blur-sm" style={{background: 'rgba(232,93,4,0.14)', border: '1px solid rgba(232,93,4,0.28)'}}>
            <div className="flex -space-x-2">
              {['ZD', 'PM', 'TN', 'GS'].map((initials) => (
                <div
                  key={`avatar-${initials}`}
                  className="w-8 h-8 rounded-full flex items-center justify-center border-2 text-xs font-bold text-white"
                  style={{background: 'var(--primary)', borderColor: '#1A0A00'}}
                >
                  {initials}
                </div>
              ))}
            </div>
            <div>
              <p className="text-white font-semibold text-sm">{p('auth.stats.count')}</p>
              <p className="text-xs" style={{color: 'rgba(245,240,232,0.55)'}}>{p('auth.stats.subtitle')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel — styled with faint border and nice background */}
      <div className="flex-1 flex flex-col min-h-screen overflow-y-auto" style={{background: 'linear-gradient(135deg, #f8f4ef 0%, #fdf9f5 40%, #f0f4f8 100%)'}}>
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 px-6 pt-6 pb-4">
          <AppLogo size={32} />
          <span className="font-extrabold text-xl text-primary">CVMaids</span>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 xl:px-16 py-8">
          <div className="w-full max-w-md mx-auto">
            {/* Form card with faint border */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-orange-100/60 px-7 py-8">
              {/* Mode tabs */}
              <div className="mb-7">
                <h2 className="text-2xl font-bold text-foreground mb-1">
                  {mode === 'login' ? p('auth.login.title') : p('auth.register.title')}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {mode === 'login' ? p('auth.login.subtitle') : p('auth.register.subtitle')}
                </p>
              </div>

              {/* Top-level Login / Register switcher */}
              <div className="flex rounded-xl p-1 mb-6" style={{background: 'rgba(232,93,4,0.07)', border: '1px solid rgba(232,93,4,0.12)'}}>
                <button
                  suppressHydrationWarning
                  onClick={() => setMode('login')}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    mode === 'login' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  style={mode === 'login' ? {border: '1px solid rgba(232,93,4,0.15)'} : {}}
                >
                  {p('auth.signin.button')}
                </button>
                <button
                  suppressHydrationWarning
                  onClick={() => setMode(mode === 'login' ? 'register-employer' : mode)}
                  className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    mode !== 'login' ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  style={mode !== 'login' ? {border: '1px solid rgba(232,93,4,0.15)'} : {}}
                >
                  {p('auth.register.button')}
                </button>
              </div>

              {/* Register sub-tabs */}
              {mode !== 'login' && (
                <div className="flex rounded-xl mb-6 overflow-hidden" style={{border: '1px solid rgba(232,93,4,0.18)'}}>
                  <button
                    onClick={() => setMode('register-employer')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all duration-200 ${
                      mode === 'register-employer' ? 'text-white' : 'bg-white/60 text-muted-foreground hover:bg-orange-50'
                    }`}
                    style={mode === 'register-employer' ? {background: 'var(--primary)'} : {}}
                  >
                    <Home size={16} />
                    {p('auth.employer.tab')}
                  </button>
                  <button
                    onClick={() => setMode('register-hm')}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-all duration-200 ${
                      mode === 'register-hm' ? 'text-white' : 'bg-white/60 text-muted-foreground hover:bg-orange-50'
                    }`}
                    style={mode === 'register-hm' ? {background: 'var(--primary)'} : {}}
                  >
                    <Users size={16} />
                    {p('auth.hm.tab')}
                  </button>
                </div>
              )}

              {/* Form content */}
              <div className="animate-fade-in">
                {mode === 'login' && <LoginForm onSwitchToRegister={() => setMode('register-employer')} />}
                {mode === 'register-employer' && <EmployerRegisterForm onSwitchToLogin={() => setMode('login')} />}
                {mode === 'register-hm' && <HomeManagerRegisterForm onSwitchToLogin={() => setMode('login')} />}
              </div>
            </div>

            <p className="text-center text-xs text-muted-foreground mt-5">
              {p('auth.terms.text')}{' '}
              <Link href="#" className="text-primary hover:underline">Terms of Service</Link>{' '}
              and{' '}
              <Link href="#" className="text-primary hover:underline">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}